import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Group, Skeleton, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { APP_PREVIEW_QUERY_KEYS } from '@/lib/constants/appPreview';
import type { AppCfgsDto } from '@/lib/types/App';
import type { AppV1Cfg, AppV1Item, AppV1Row } from '@/lib/types/AppV1';
import { AppPreviewUtils } from '../AppPreviewUtils';
import { useAppPreviewBrowse, useAppPreviewCfg } from '../useAppPreview';
import { AppTvBillboard, BILLBOARD_HEIGHT_FHD, NAVBAR_HEIGHT_FHD } from './AppTvBillboard';

interface AppTvPreviewProps {
  app: AppCfgsDto;
}

/**
 * In-browser TV frame rendering the app's LIVE /app/v1 cfg + browse — the same
 * payloads the Roku shell loads (WS-2.3 app preview, transport per D-2.6).
 * It mirrors the shell's home screen (design: ott/design system/surfaces.md
 * "Home"): NavBar strip, hero billboard per the server's heroStyle, content
 * rails — real theme, real rows, real artwork; hovering simulates remote focus.
 */
export function AppTvPreview({ app }: AppTvPreviewProps) {
  const queryClient = useQueryClient();
  const appDomain = AppPreviewUtils.appDomain(app);
  const cfgQuery = useAppPreviewCfg(appDomain);
  const browseQuery = useAppPreviewBrowse(appDomain);

  if (!appDomain) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        The preview needs your app&apos;s domain. Set one up on the Web tab first.
      </Alert>
    );
  }

  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: APP_PREVIEW_QUERY_KEYS.all });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Text fw={600} size="lg">
            App Preview
          </Text>
          <Text size="sm" c="dimmed">
            Rendered from your app&apos;s live configuration — the same data your TV app
            loads. Recent changes can take a couple of minutes to appear.
          </Text>
        </div>
        <Button
          variant="default"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          onClick={handleRefresh}
          loading={cfgQuery.isFetching || browseQuery.isFetching}
        >
          Refresh
        </Button>
      </Group>

      {cfgQuery.isError || browseQuery.isError ? (
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          The preview could not load your app&apos;s configuration. If the app or its
          domain was just enabled, give it a couple of minutes and refresh. The app must
          be Active for its content service to answer.
        </Alert>
      ) : cfgQuery.data && browseQuery.data ? (
        <TvFrame cfg={cfgQuery.data} rows={AppPreviewUtils.visibleRows(browseQuery.data)} />
      ) : (
        <Skeleton radius="lg" style={{ aspectRatio: '16 / 9', width: '100%' }} />
      )}
    </Stack>
  );
}

// ── Internal: the frame itself. Colors/spacing come from the live theme
// payload, so everything below is inline-styled — Mantine theming must not
// leak into the simulated TV canvas. ──

interface TvFrameProps {
  cfg: AppV1Cfg;
  rows: AppV1Row[];
}

function TvFrame({ cfg, rows }: TvFrameProps) {
  const { colors } = cfg.theme;
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const { fhd } = AppPreviewUtils;
  const heroRow = AppPreviewUtils.heroRow(rows);
  const railRows = AppPreviewUtils.railRows(rows);

  // NavBar (rokucore nav/NavBar.xml): opaque 1920x120 strip, brand logo at
  // [96,10] 400x100, tabs from x=560 with 48px spacing. Over a billboard the
  // strip occludes the zone's top 120px (adr_0002), so it is absolutely placed.
  const navBar = (
    <div
      style={{
        position: heroRow ? 'absolute' : 'relative',
        top: 0,
        left: 0,
        width: '100%',
        height: fhd(NAVBAR_HEIGHT_FHD),
        backgroundColor: colors.bgPrimary,
        display: 'flex',
        alignItems: 'center',
        zIndex: 1,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: fhd(96),
          top: fhd(10),
          width: fhd(400),
          height: fhd(100),
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {cfg.brand.logoUrl ? (
          <img
            src={cfg.brand.logoUrl}
            alt={cfg.brand.name}
            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span
            style={{
              color: colors.textPrimary,
              fontSize: fhd(36),
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {cfg.brand.name}
          </span>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          left: fhd(560),
          top: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: fhd(48),
        }}
      >
        {cfg.nav.map((navItem, idx) => (
          <span
            key={navItem.navId}
            style={{
              color: idx === 0 ? colors.accent : colors.textSecondary,
              fontSize: fhd(30),
              fontWeight: idx === 0 ? 600 : 400,
              whiteSpace: 'nowrap',
            }}
          >
            {navItem.title}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div
      style={{
        aspectRatio: '16 / 9',
        width: '100%',
        maxWidth: 960,
        backgroundColor: colors.bgPrimary,
        border: '10px solid #17181c',
        borderRadius: 14,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        overflowY: 'auto',
        overflowX: 'hidden',
        // A TV has no scrollbar: the second-row peek is the "more below" signal.
        scrollbarWidth: 'none',
        // FHD geometry below is expressed in cqw units against this frame.
        containerType: 'inline-size',
        position: 'relative',
      }}
    >
      {navBar}
      {heroRow && <AppTvBillboard cfg={cfg} row={heroRow} />}

      {/* rails (rokucore HomeView/RowList): 96px content margin, rows clip at
          x=1824 like the RowList's 1728px row, 24px art-to-art gap, 40px
          inter-row spacing + 24px row pad; 24px minimum air under the
          billboard (adr_0002). No scrollbar: a TV row simply runs off the
          right edge (trackpad/shift-wheel still scrolls it). */}
      <div
        style={{
          // Hero state: Rows.bs HeroRowsY (second-row peek at the fold);
          // browse state: rows at y=180 under the 120px NavBar.
          paddingTop: heroRow
            ? fhd(AppPreviewUtils.heroRowsY(railRows.map(AppPreviewUtils.rowHeightFhd), railRows.length) - BILLBOARD_HEIGHT_FHD)
            : fhd(60),
          paddingBottom: fhd(48),
          paddingLeft: fhd(96),
        }}
      >
        {rows.length === 0 && (
          <span style={{ color: colors.textSecondary, fontSize: fhd(27) }}>
            No content rows yet — organize media into categories to fill the home screen.
          </span>
        )}

        {railRows.map((row) => {
          const size = AppPreviewUtils.cardSizeFhd(row.itemAspect);
          const belowCard = row.titleTreatment === 'below_card';
          return (
            <div key={row.rowId} style={{ marginBottom: fhd(14) }}>
              {row.title && (
                <div
                  style={{
                    color: colors.textPrimary,
                    fontSize: fhd(30),
                    lineHeight: fhd(36),
                    fontWeight: 500,
                    marginBottom: fhd(14),
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {row.title}
                </div>
              )}
              <div
                style={{
                  width: fhd(1728),
                  display: 'flex',
                  gap: fhd(24),
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  scrollbarWidth: 'none',
                  // Room for the outside focus ring (2px gap + 4px ring).
                  padding: fhd(6),
                  margin: `-${fhd(6)}`,
                  // Below-card titles reserve a measured 2-line block (104px).
                  minHeight: belowCard ? fhd(size.h + 104) : fhd(size.h),
                }}
              >
                {row.items.map((rowItem) => (
                  <TvCard
                    key={rowItem.idBC}
                    cfg={cfg}
                    row={row}
                    item={rowItem}
                    size={size}
                    focused={focusedKey === `${row.rowId}:${rowItem.idBC}`}
                    onFocus={() => setFocusedKey(`${row.rowId}:${rowItem.idBC}`)}
                    onBlur={() => setFocusedKey(null)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface TvCardProps {
  cfg: AppV1Cfg;
  row: AppV1Row;
  item: AppV1Item;
  size: { w: number; h: number };
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Mirror of rokucore CardItem: square crisp art (adr_0004), badge at [10,10]
 * 30px tall, below-card title in the small system font 12px under the art
 * (max 2 lines), and the focus ring OUTSIDE the art — 2px page gap + ring —
 * with no card scale-up (adr_0007 v2).
 */
function TvCard({ cfg, row, item, size, focused, onFocus, onBlur }: TvCardProps) {
  const { colors, focus } = cfg.theme;
  const { fhd } = AppPreviewUtils;
  const artworkUrl = AppPreviewUtils.artworkUrl(item, row.itemAspect);
  const ringPx = Math.min(focus.ringWidthPx, 4);
  const badge = item.badge;
  const badgeBg =
    badge === 'live' ? colors.badgeLive : badge === 'new' ? colors.badgeNew : colors.bgOverlay;

  return (
    <div
      onMouseEnter={onFocus}
      onMouseLeave={onBlur}
      style={{ flex: '0 0 auto', width: fhd(size.w), cursor: 'default' }}
    >
      <div
        style={{
          position: 'relative',
          width: fhd(size.w),
          height: fhd(size.h),
          backgroundColor: colors.bgSurface,
          overflow: 'hidden',
          outline: focused ? `${fhd(ringPx)} solid ${colors.focusRing}` : 'none',
          outlineOffset: fhd(2),
        }}
      >
        {artworkUrl ? (
          <img
            src={AppPreviewUtils.sizedArtworkUrl(artworkUrl, 640)}
            alt={item.title}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.textSecondary,
              fontSize: fhd(24),
              padding: fhd(12),
              textAlign: 'center',
            }}
          >
            {item.title}
          </div>
        )}
        {badge && (
          <div
            style={{
              position: 'absolute',
              top: fhd(10),
              left: fhd(10),
              height: fhd(30),
              padding: `0 ${fhd(11)}`,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: badgeBg,
              color: colors.textOnAccent,
              fontSize: fhd(22),
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {badge}
          </div>
        )}
      </div>
      {row.titleTreatment === 'below_card' && (
        <div
          style={{
            color: focused ? colors.textPrimary : colors.textSecondary,
            fontSize: fhd(27),
            lineHeight: fhd(40),
            marginTop: fhd(12),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.title}
        </div>
      )}
    </div>
  );
}
